import { useState, useMemo } from "react";
import { COLLECTIONS } from "../data/collections";
import { PERSONALITIES } from "../data/site";
import CollectionGrid from "../components/CollectionGrid";
import Reveal from "../components/Reveal";

const AGE_BUCKETS = [
  { label: "All", test: () => true },
  { label: "2–4", test: (a) => /^[2-4]/.test(a) },
  { label: "5–7", test: (a) => /[5-7]/.test(a) },
  { label: "8–10", test: (a) => /[8-9]|10/.test(a) },
];

export default function AllCollections() {
  const [personality, setPersonality] = useState(null);
  const [age, setAge] = useState("All");

  const filtered = useMemo(() => {
    return COLLECTIONS.filter((c) =>
      (!personality || c.personality === personality) &&
      AGE_BUCKETS.find((b) => b.label === age).test(c.age)
    );
  }, [personality, age]);

  return (
    <div className="pt-32 px-5 md:px-10" data-testid="all-collections-page">
      <div className="mx-auto max-w-[1440px] pb-10 md:pb-14">
        <Reveal>
          <p className="eyebrow mb-4">All collections</p>
        </Reveal>
        <Reveal delay={60}>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[112px] leading-[0.95] tracking-[-0.03em] max-w-[18ch]">
            Every frame, <span className="italic">a personality.</span>
          </h1>
        </Reveal>

        {/* Filters */}
        <div className="mt-10 flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="eyebrow mr-2">Age</span>
            {AGE_BUCKETS.map((b) => (
              <button
                key={b.label}
                onClick={() => setAge(b.label)}
                className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${age === b.label ? "bg-[#2C2C2A] text-[#FAFAF7]" : "bg-[#F3EADA] text-[#2C2C2A] hover:bg-[#E8DCC4]"}`}
                data-testid={`filter-age-${b.label}`}
              >
                {b.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="eyebrow mr-2">Personality</span>
            <button
              onClick={() => setPersonality(null)}
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${!personality ? "bg-[#2C2C2A] text-[#FAFAF7]" : "bg-[#F3EADA] text-[#2C2C2A] hover:bg-[#E8DCC4]"}`}
              data-testid="filter-personality-all"
            >
              All
            </button>
            {PERSONALITIES.map((p) => (
              <button
                key={p.slug}
                onClick={() => setPersonality(p.slug)}
                className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${personality === p.slug ? "bg-[#2C2C2A] text-[#FAFAF7]" : "bg-[#F3EADA] text-[#2C2C2A] hover:bg-[#E8DCC4]"}`}
                data-testid={`filter-personality-${p.slug}`}
              >
                {p.emoji} {p.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-24" data-testid="empty-state">
          <p className="font-serif text-3xl">Nothing matches just yet.</p>
          <p className="text-[#595955] mt-2">Try a different age or personality.</p>
          <button onClick={() => { setPersonality(null); setAge("All"); }} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C2C2A] text-[#FAFAF7] text-sm">Reset filters</button>
        </div>
      ) : (
        <CollectionGrid items={filtered} title={`${filtered.length} frames`} subtitle={`${age === "All" ? "All ages" : `Ages ${age}`}${personality ? ` · ${PERSONALITIES.find((p) => p.slug === personality)?.title}` : ""}`} />
      )}
    </div>
  );
}
